import { API_URL } from '../config';

type RequestInit = {
  method?: string;
  body?: unknown;
  token?: string;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public requestId?: string,
    public fields?: Record<string, string>,
    /**
     * The server's `error` slug from the JSON body, e.g. 'plan_limit_reached'
     * / 'captcha_failed' / 'account_suspended'. Used by UIs that need to
     * differentiate between several 4xx outcomes — e.g. MatchAdd's full-
     * screen PlanCapReached overlay only fires when code === 'plan_limit_reached'.
     */
    public code?: string,
  ) {
    super(message);
  }
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (init.token) headers.Authorization = `Bearer ${init.token}`;

  const res = await fetch(`${API_URL}${path}`, {
    method: init.method ?? 'GET',
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    credentials: 'omit',
  });

  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = { error: 'non_json', message: text.slice(0, 200) }; }

  if (!res.ok) {
    const body = parsed as { error?: string; message?: string; requestId?: string; fields?: Record<string, string> };
    throw new ApiError(res.status, body.message ?? body.error ?? `HTTP ${res.status}`, body.requestId, body.fields, body.error);
  }
  return parsed as T;
}
